import { datadogRum } from '@datadog/browser-rum'
import { OperationOutcomeError } from '@medplum/core'
import type { BundleRetryEvent } from '@seen/core/client'

interface PutConflictEvent {
  resourceType: string
  id: string
  fromVersionId: string | undefined
  toVersionId: string | undefined
}

// Fired when `SeenMedplumClient.executeBatch` retries a transaction after
// a versionId test failure. Wired via the `onBundleRetry` client option at
// app init — `@seen/core` can't import `@datadog/browser-rum` directly.
//
// Datadog query: `@action.name:"fhir.bundle.retry"`
export function reportBundleRetry(event: BundleRetryEvent): void {
  datadogRum.addAction('fhir.bundle.retry', {
    attempt: event.attempt,
    entries: event.entries,
  })
}

interface AcceptSideEffectFailureEvent {
  suggestionId: string
  nodeId: string
  nodeType: string
  error: unknown
}

interface ClassifyErrorResult {
  errorType: string
  issueCodes: string[]
}

// The thrown value reduced to fields whose shape we control. A server error's
// own message is free text — for a FHIR failure it is the OperationOutcome's
// diagnostics, which can quote the resource that failed validation — so it is
// deliberately never forwarded to a telemetry sink. `issue.code` is a closed
// FHIR value set and carries the diagnostic signal without the payload.
//
// The FHIR case is named by `instanceof` rather than read off `constructor.name`,
// which minifies to a single letter in a built bundle.
function classifyError(cause: unknown): ClassifyErrorResult {
  if (cause instanceof OperationOutcomeError) {
    return {
      errorType: 'OperationOutcomeError',
      issueCodes: (cause.outcome.issue ?? []).map((issue) => issue.code),
    }
  }
  return {
    errorType: cause instanceof Error ? cause.constructor.name : typeof cause,
    issueCodes: [],
  }
}

// Fired when an AI-suggestion accept's `applyOnAccept` throws. The pipeline
// swallows the throw by design (R4: a failed side effect degrades the node to
// draft-flagged rather than bouncing the clinician's accept), so this is the
// ONLY record that the durable half of an accept — for a condition mention, the
// Encounter.diagnosis link — never landed.
//
// Every field carries a shape this code controls: a stamp identity, a node type,
// and the error reduced to a class name plus FHIR issue codes. The thrown
// error's own message is deliberately absent — see `classifyError`. Both sinks
// matter, because the portal forwards console output to Datadog too, so a raw
// message here would reach it twice.
//
// Datadog query: `@action.name:"ai.accept.side_effect_failed"`
export function reportAcceptSideEffectFailure(
  event: AcceptSideEffectFailureEvent
): void {
  const { errorType, issueCodes } = classifyError(event.error)
  const codes = issueCodes.length > 0 ? ` (${issueCodes.join(', ')})` : ''
  console.warn(
    `[ai-suggestions] accept side effect failed for ${event.nodeType} ` +
      `${event.nodeId}: ${errorType}${codes}`
  )
  datadogRum.addAction('ai.accept.side_effect_failed', {
    suggestionId: event.suggestionId,
    nodeId: event.nodeId,
    nodeType: event.nodeType,
    errorType,
    issueCodes,
  })
}

interface AcceptStampsMissingEvent {
  suggestionId: string
  nodeIds: string[]
}

// Fired when an accept's side-effect pass finds NONE of the stamped nodes its
// own receipt recorded. R1 persists the receipt before side effects, and that
// await is a full FHIR round trip — so anything that removes the just-inserted
// content inside that window (an undo, a content reset) makes the side-effect
// pass a no-op: no encounter link, no createdRef, and the status projection
// bails on the missing stamp, leaving the record pending and re-painted as an
// undecided suggestion. The clinician sees an accept that did nothing, and
// without this there is no signal anywhere that it happened.
//
// Datadog query: `@action.name:"ai.accept.stamps_missing"`
export function reportAcceptStampsMissing(
  event: AcceptStampsMissingEvent
): void {
  console.warn(
    `[ai-suggestions] accept ${event.suggestionId} found none of its ` +
      `${event.nodeIds.length} stamped node(s) — content was removed mid-accept`
  )
  datadogRum.addAction('ai.accept.stamps_missing', {
    suggestionId: event.suggestionId,
    nodeIds: event.nodeIds,
  })
}

// Fired when a PUT with an `If-Match` header fails with 412 Precondition
// Failed — i.e., the resource was modified between our read and write.
//
// Datadog query: `@action.name:"fhir.put.conflict"`
export function reportPutConflict(event: PutConflictEvent): void {
  console.warn(
    `[fhir] put conflict ${event.resourceType}/${event.id} ` +
      `from=${event.fromVersionId ?? 'unknown'} → to=${event.toVersionId ?? 'unknown'}`
  )
  datadogRum.addAction('fhir.put.conflict', {
    resourceType: event.resourceType,
    resourceId: event.id,
    fromVersionId: event.fromVersionId,
    toVersionId: event.toVersionId,
  })
}
